import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  LegalEntityForSelectFragment,
  LegalEntitySelectorDocument,
  LegalEntitySelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import i18n from 'modules/core/lib/i18n'

import { LegalEntityMultiSelectorProps, LegalEntitySelectorProps } from './types'

interface ItemProps {
  legalEntity: LegalEntityForSelectFragment
  showCurrency?: boolean
}
const LegalEntityItem = ({ legalEntity, showCurrency }: ItemProps) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center" key={legalEntity.id}>
      <Text.Normal size="300">
        {showCurrency
          ? `${legalEntity.legalName} - [${legalEntity.currency}]`
          : legalEntity.legalName}
      </Text.Normal>
    </Box>
  )
}
const LegalEntitySkeleton = () => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center">
      <Skeleton width="s240" height="s32" />
    </Box>
  )
}

export const LegalEntitySelector = (
  props: LegalEntitySelectorProps | LegalEntityMultiSelectorProps
) => {
  const extractConnection = (data: LegalEntitySelectorQuery) => {
    return data.companies.legalEntitiesConnection
  }
  const renderEntity = (entity: LegalEntityForSelectFragment) => (
    <LegalEntityItem legalEntity={entity} showCurrency={props.showCurrency} />
  )
  const renderSkeleton = () => <LegalEntitySkeleton />

  const selectorConfig = {
    variables: {
      excludeIds: props.excludeIds,
    },
    document: LegalEntitySelectorDocument,
    extractConnection,
    renderEntity,
    renderSkeleton,
    filterEntities: (entities: LegalEntityForSelectFragment[]) => {
      if (props.currency) {
        return entities.filter((entity) => entity.currency === props.currency)
      }
      return entities
    },
  }

  if (props.multiple) {
    const selectorProps = { ...props, ...selectorConfig }
    return (
      <EntitySelector.Multiple
        {...selectorProps}
        renderValue={(entities) => (
          <Text.Normal ellipsis>
            {entities.length > 0
              ? entities.map((legalEntity) => legalEntity.legalName).join(', ')
              : props.emptyLabel ?? i18n.t('common.select_multiple')}
          </Text.Normal>
        )}
      />
    )
  }

  const selectorProps = { ...props, ...selectorConfig }
  return (
    <EntitySelector
      {...selectorProps}
      renderValue={(entity) => (
        <Text.Normal ellipsis>
          {entity !== null ? entity.legalName : props.emptyLabel ?? i18n.t('common.select')}
        </Text.Normal>
      )}
    />
  )
}
